import { Circle, CircleCheck, CircleDot, ListChecks } from "lucide-react";

import { cn } from "@bigpp/ui/lib/utils";

import type { PlanItemStatus } from "../agent/useAgentRun.ts";

type Item = { text: string; status: PlanItemStatus };

// Checklist-style plan modeled on AI SDK Elements' Plan. Each item shows a
// status icon (pending / in-progress / completed). In-progress rows shimmer
// so the user can see at a glance what the agent is doing right now.
export function Plan({ title, items }: { title?: string; items: Item[] }) {
  const done = items.filter((i) => i.status === "completed").length;
  return (
    <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden">
      <div className="flex items-center gap-2 px-2.5 py-1.5 border-b border-[var(--color-border)]">
        <ListChecks className="size-3.5 text-[var(--color-muted-foreground)]" />
        <span className="text-xs font-medium text-[var(--color-foreground)] truncate">
          {title ?? "Plan"}
        </span>
        <span className="ml-auto text-[10px] tabular-nums text-[var(--color-muted-foreground)]">
          {done}/{items.length}
        </span>
      </div>
      <ul className="flex flex-col">
        {items.map((it, i) => (
          <PlanRow key={i} item={it} />
        ))}
      </ul>
    </div>
  );
}

function PlanRow({ item }: { item: Item }) {
  const Icon =
    item.status === "completed"
      ? CircleCheck
      : item.status === "in_progress"
        ? CircleDot
        : Circle;
  const iconClass =
    item.status === "completed"
      ? "text-[var(--color-success)]"
      : item.status === "in_progress"
        ? "text-[var(--color-primary)]"
        : "text-[var(--color-muted-foreground)]";
  return (
    <li className="flex items-start gap-2 px-2.5 py-1 text-xs">
      <Icon
        className={cn(
          "size-3.5 shrink-0 mt-0.5",
          iconClass,
          item.status === "in_progress" && "animate-pulse",
        )}
      />
      <span
        className={cn(
          "leading-snug break-words min-w-0",
          item.status === "completed" &&
            "line-through text-[var(--color-muted-foreground)]",
          item.status === "in_progress" && "shimmer-text",
          item.status === "pending" && "text-[var(--color-muted-foreground)]",
        )}
      >
        {item.text}
      </span>
    </li>
  );
}
